import React, { useEffect, useRef, useState } from 'react';
import { countWorkHours } from '../functions/countWorkHours';
import { getDarboDienaWord } from '../functions/inflection';

type PeriodSummaryProps = {
  periods: { start: string; end: string }[];
  holidayDates: Map<string, string>;
  workWeek: 'five' | 'six';
};

const PeriodSummary = ({ periods, holidayDates, workWeek }: PeriodSummaryProps) => {
  const sortedPeriods = [...periods].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
  );

  const countWorkdaysInRange = (startIso: string, endIso: string) => {
    const start = new Date(startIso);
    const end = new Date(endIso);
    const s = start <= end ? new Date(start) : new Date(end);
    const e = start <= end ? new Date(end) : new Date(start);
    let count = 0;
    const cur = new Date(s);
    while (cur <= e) {
      const yyyy = cur.getFullYear();
      const mm = String(cur.getMonth() + 1).padStart(2, '0');
      const dd = String(cur.getDate()).padStart(2, '0');
      const key = `${yyyy}-${mm}-${dd}`;

      const dow = cur.getDay();

      if (!holidayDates.has(key)) {
        if (workWeek === 'five') {
          if (dow >= 1 && dow <= 5) count += 1;
        } else {
          if (dow >= 1 && dow <= 6) count += 1;
        }
      }

      cur.setDate(cur.getDate() + 1);
    }

    return count;
  };

  const containerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [isFixed, setIsFixed] = useState(false);
  const [placeholderHeight, setPlaceholderHeight] = useState<number>(0);
  const [leftPx, setLeftPx] = useState<number | null>(null);
  const [widthPx, setWidthPx] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const el = containerRef.current;
    const sentinel = sentinelRef.current;
    if (!el || !sentinel) return;

    let roRaf: number | null = null;

    const ro = new (window as any).ResizeObserver((entries: any[]) => {
      for (const entry of entries) {
        const current = containerRef.current;
        if (!current) return;
        const h = entry.contentRect?.height ?? current.getBoundingClientRect().height;
        setPlaceholderHeight(h);

        if (isFixed) {
          if (roRaf != null) continue;
          roRaf = window.requestAnimationFrame(() => {
            roRaf = null;
            const r = current.getBoundingClientRect();
            setLeftPx(r.left);
            setWidthPx(r.width);
          });
        }
      }
    });
    ro.observe(el);

    const headerHeightRaw = getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '64px';
    const headerHeight = parseInt(headerHeightRaw.replace('px', ''), 10) || 64;
    const offset = headerHeight + 8;

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const shouldFix = !entry.isIntersecting;
        const current = containerRef.current;
        if (shouldFix && !isFixed) {
          if (!current) return;
          // use IntersectionObserverEntry.boundingClientRect to avoid another sync layout
          const b = entry && (entry as IntersectionObserverEntry).boundingClientRect;
          if (b) {
            setLeftPx(b.left);
            setWidthPx(b.width);
          } else {
            const r = current.getBoundingClientRect();
            setLeftPx(r.left);
            setWidthPx(r.width);
          }
          setIsFixed(true);
        } else if (!shouldFix && isFixed) {
          setIsFixed(false);
          setLeftPx(null);
          setWidthPx(null);
        }
      },
      {
        root: null,
        rootMargin: `-${offset}px 0px 0px 0px`,
        threshold: 0,
      }
    );

    io.observe(sentinel);

    let resizeRaf: number | null = null;
    function onWindowResize() {
      if (resizeRaf != null) return;
      resizeRaf = window.requestAnimationFrame(() => {
        resizeRaf = null;
        const current = containerRef.current;
        if (!current) return;
        const r = current.getBoundingClientRect();
        setPlaceholderHeight(r.height);
        if (isFixed) {
          setLeftPx(r.left);
          setWidthPx(r.width);
        }
      });
    }
    window.addEventListener('resize', onWindowResize);

    return () => {
      if (roRaf != null) {
        window.cancelAnimationFrame(roRaf);
        roRaf = null;
      }
      if (resizeRaf != null) {
        window.cancelAnimationFrame(resizeRaf);
        resizeRaf = null;
      }
      ro.disconnect();
      io.disconnect();
      window.removeEventListener('resize', onWindowResize);
    };
  }, [isFixed]);

  return (
    <>
      <div ref={sentinelRef} style={{ position: 'relative', height: 1, width: '100%' }} aria-hidden />
      {isFixed && <div style={{ height: placeholderHeight }} aria-hidden />}

      <div
        ref={containerRef}
        className={`${'mx-auto p-4 bg-green-100 rounded text-center text-green-900 font-semibold shadow'} ${!isFixed ? 'w-full max-w-4xl' : ''}`}
        style={
          isFixed
            ? {
              position: 'fixed',
              top: 'calc(var(--header-height) + 8px)',
              left: leftPx != null ? `${leftPx}px` : undefined,
              width: widthPx != null ? `${widthPx}px` : undefined,
              zIndex: 2000,
              willChange: 'transform',
            }
            : undefined
        }
      >
        <h3 className="mb-2">Pažymėtos datos:</h3>

        {sortedPeriods.length === 0 && <p>Jokių pažymėtų datų.</p>}

        {sortedPeriods.map(({ start, end }, idx) => {
          const hours = countWorkHours(start, end, holidayDates, workWeek);
          const days = countWorkdaysInRange(start, end);

          return (
            <p key={idx}>
              {start} – {end} ({days} {getDarboDienaWord(days)}, {hours} val.)
            </p>
          );
        })}
      </div>
    </>
  );
};

export default PeriodSummary;
